import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/png phoenix dental.png';
import { FaTooth, FaSmile, FaStar, FaHeart, FaAward } from 'react-icons/fa';

const slides = [
  {
    title: "Transform Your Smile",
    subtitle: "Advanced Dental Care for a Radiant Future",
    icon: <FaSmile className="text-6xl text-phoenix-gold" />,
  },
  {
    title: "Expert Dental Services",
    subtitle: "State-of-the-Art Facilities & Personalized Treatment",
    icon: <FaTooth className="text-6xl text-phoenix-gold" />,
  },
  {
    title: "Your Health, Our Priority",
    subtitle: "Comprehensive Care with Compassionate Professionals",
    icon: <FaHeart className="text-6xl text-phoenix-gold" />,
  },
];

const Particle = ({ delay }) => (
  <motion.div
    className="absolute w-2 h-2 bg-phoenix-gold rounded-full opacity-30"
    style={{
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
    }}
    animate={{
      y: [0, -100, 0],
      x: [0, Math.random() * 100 - 50, 0],
      opacity: [0.3, 0.8, 0.3],
    }}
    transition={{
      duration: 8,
      repeat: Infinity,
      delay: delay,
    }}
  />
);

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" className="relative min-h-screen bg-gradient-to-br from-phoenix-beige via-phoenix-white to-phoenix-beige overflow-hidden flex items-center">
      {/* Animated Particles */}
      {[...Array(20)].map((_, i) => (
        <Particle key={i} delay={i * 0.2} />
      ))}

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dental-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <circle cx="50" cy="50" r="2" fill="#d4af37" />
              <path d="M30 30 Q50 10 70 30 Q90 50 70 70 Q50 90 30 70 Q10 50 30 30" stroke="#d4af37" strokeWidth="1" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dental-pattern)" />
        </svg>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="text-center">
          <motion.div
            className="mb-8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8, type: "spring" }}
          >
            <img src={logo} alt="Phoenix Dental Logo" className="w-48 h-48 mx-auto mb-4" />
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="flex justify-center mb-4">
                {slides[currentSlide].icon}
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-phoenix-black mb-4 leading-tight">
                {slides[currentSlide].title.split(' ').map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                    className="inline-block mr-2"
                  >
                    {word}
                  </motion.span>
                ))}
              </h1>
              <motion.p
                className="text-xl lg:text-2xl text-phoenix-gray mb-8 max-w-3xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
              >
                {slides[currentSlide].subtitle}
              </motion.p>
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: '0 20px 40px rgba(212, 175, 55, 0.4)',
                backgroundColor: '#b8973a'
              }}
              whileTap={{ scale: 0.95 }}
              className="bg-phoenix-gold text-phoenix-white px-10 py-4 rounded-full text-xl font-bold hover:bg-opacity-90 transition-all duration-300 shadow-xl"
            >
              Book Appointment
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: '#d4af37', color: '#ffffff' }}
              whileTap={{ scale: 0.95 }}
              className="border-3 border-phoenix-gold text-phoenix-gold px-10 py-4 rounded-full text-xl font-bold hover:bg-phoenix-gold hover:text-phoenix-white transition-all duration-300"
            >
              Our Services
            </motion.button>
          </motion.div>

          {/* Slide Indicators */}
          <motion.div
            className="flex justify-center mt-8 space-x-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.5, duration: 0.8 }}
          >
            {slides.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full ${
                  index === currentSlide ? 'bg-phoenix-gold' : 'bg-phoenix-gray'
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-phoenix-gold rounded-full flex justify-center">
          <motion.div
            className="w-1 h-3 bg-phoenix-gold rounded-full mt-2"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
